/**
 * Batches — the re-engagement batch table with its C-19 target drill-through,
 * extracted verbatim from AgentConsole's batches tab (Phase-5 prep) so the v2
 * console's Manage tab can re-house the SAME component instead of re-implementing
 * it. Purely presentational: the drill-through state (openBatchId/batchDetail)
 * and every mutation stay on the page, so tab switches keep their state exactly
 * as they always have.
 */
import { Fragment } from 'react';
import { Megaphone, Loader2, ChevronRight, Ban, MessageSquareText } from 'lucide-react';
import { RowLink } from '../../components/shared';
import { ago } from '../../components/agent/shared';

export interface BatchRow {
  id: string;
  study_id: string;
  study_title: string | null;
  source: { description?: string; search_run_id?: string } | null;
  opener_template_key: string;
  pace: { per_hour?: number } | null;
  status: string;
  created_at: string;
  launched_at: string | null;
  counts: {
    targets: number; pending: number; sent: number; replied: number;
    expired: number; canceled: number; blocked: number; skipped: number;
  };
}

/** C-19: one row of GET /agent/batches/:id — the drill-through payload. */
export interface BatchTargetRow {
  id: string;
  contact_id: string;
  contact_name: string | null;
  state: string;
  replied_at: string | null;
  conversation_id: string | null;
  send_at: string | null;
  sent_at: string | null;
}

/** C-19: batch-target state dots for the drill-through list. */
const TARGET_DOT: Record<string, string> = {
  replied: 'bg-green-500',
  sent: 'bg-primary-500',
  pending: 'bg-amber-400',
  blocked: 'bg-red-400',
  expired: 'bg-gray-300 dark:bg-gray-600',
  canceled: 'bg-gray-300 dark:bg-gray-600',
  skipped: 'bg-gray-300 dark:bg-gray-600',
};

export default function BatchesPanel({
  batches, agentEnabled, busyId, openBatchId, batchDetail, onToggleBatch, onCancelBatch, onOpenConversation,
}: {
  batches: BatchRow[];
  agentEnabled: boolean;
  busyId: string | null;
  openBatchId: string | null;
  batchDetail: { id: string; targets: BatchTargetRow[] } | null;
  onToggleBatch: (id: string) => void;
  onCancelBatch: (id: string) => void;
  onOpenConversation: (conversationId: string) => void;
}) {
  return (
    <div className="mt-3">
      {!agentEnabled && batches.some((b) => b.status === 'active' || b.status === 'launching') && (
        <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          The texting agent is <strong>off</strong>, but active batches keep sending their scheduled
          openers and replies will wait unanswered in needs-reply. Cancel the batches below if the
          outreach should stop too.
        </div>
      )}
      {batches.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <Megaphone className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="text-lg font-medium text-gray-900 dark:text-gray-100">No re-engagement batches</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
            Launch one from a Search run ("Agent outreach") — staff pick the cohort and the approved opener; replies open agent conversations automatically.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  {['Batch', 'Study', 'Status', 'Progress', 'Replied', 'Pace', 'Launched'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">{h}</th>
                  ))}
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {batches.map((b) => {
                  const c = b.counts;
                  const delivered = c.sent + c.replied + c.expired;
                  return (
                    <Fragment key={b.id}>
                    {/* C-19: rows drill through to their targets — the path
                        from "replied: N" to those N conversations. */}
                    <tr
                      onClick={() => onToggleBatch(b.id)}
                      aria-expanded={openBatchId === b.id}
                      className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                        <span className="flex items-start gap-1.5">
                          <ChevronRight className={`mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform duration-150 ${openBatchId === b.id ? 'rotate-90' : ''}`} />
                          <span>
                            {b.source?.description || `Batch ${b.id.slice(0, 8)}`}
                            <span className="mt-0.5 block text-xs font-normal text-gray-400">
                              {b.opener_template_key.endsWith('reengage_recent') ? 'Recent-lead opener' : 'Aged-lead opener'}
                            </span>
                          </span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{b.study_title || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          b.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                            : b.status === 'launching' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}
                        >
                          {b.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                        {delivered}/{c.targets} sent
                        {c.pending > 0 && <span className="text-xs text-gray-400"> · {c.pending} queued</span>}
                        {c.canceled > 0 && <span className="text-xs text-gray-400"> · {c.canceled} canceled</span>}
                        {c.blocked > 0 && <span className="text-xs text-gray-400"> · {c.blocked} blocked</span>}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                        {c.replied > 0 ? (
                          <span className="font-medium text-green-700 dark:text-green-400">{c.replied}</span>
                        ) : '0'}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums text-gray-500 dark:text-gray-400">{b.pace?.per_hour ?? '—'}/hr</td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{b.launched_at ? ago(b.launched_at) : '—'}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        {(b.status === 'active' || b.status === 'launching') && c.pending > 0 && (
                          <button
                            type="button"
                            disabled={busyId === b.id}
                            onClick={() => onCancelBatch(b.id)}
                            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-red-600 transition-colors duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 dark:border-gray-600 dark:text-red-400 dark:hover:bg-red-900/20 sm:min-h-0"
                          >
                            <Ban className="h-3.5 w-3.5" /> Cancel remaining
                          </button>
                        )}
                      </td>
                    </tr>
                    {openBatchId === b.id && (
                      <tr className="bg-gray-50 dark:bg-gray-900/30">
                        <td colSpan={8} className="px-4 py-3">
                          {batchDetail?.id !== b.id ? (
                            <span className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                              <Loader2 className="h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" /> Loading targets…
                            </span>
                          ) : batchDetail.targets.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400">No targets on this batch.</p>
                          ) : (
                            <>
                              <ul className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 xl:grid-cols-3">
                                {[...batchDetail.targets]
                                  // Replied (= opened a conversation) first — they're the drill-through's point.
                                  .sort((a, z) => Number(Boolean(z.conversation_id)) - Number(Boolean(a.conversation_id)))
                                  .slice(0, 150)
                                  .map((tg) => (
                                    <li key={tg.id} className="flex min-w-0 items-center gap-2 text-sm">
                                      <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${TARGET_DOT[tg.state] || 'bg-gray-300 dark:bg-gray-600'}`} />
                                      <RowLink
                                        to={`/crm/contacts/${tg.contact_id}`}
                                        className="min-w-0 truncate font-medium text-gray-900 hover:text-primary-700 dark:text-gray-100 dark:hover:text-primary-400"
                                      >
                                        {tg.contact_name || 'Lead'}
                                      </RowLink>
                                      <span className="whitespace-nowrap text-xs text-gray-400">{tg.state}</span>
                                      {tg.conversation_id && (
                                        <button
                                          type="button"
                                          onClick={() => onOpenConversation(tg.conversation_id!)}
                                          title="Open the conversation this reply started"
                                          className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                                        >
                                          <MessageSquareText className="h-3 w-3" /> conversation
                                        </button>
                                      )}
                                    </li>
                                  ))}
                              </ul>
                              {batchDetail.targets.length > 150 && (
                                <p className="mt-2 text-xs text-gray-400">
                                  Showing 150 of {batchDetail.targets.length} targets (replied first).
                                </p>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
